import React, { useState, useEffect } from 'react';
import {
  Box, Paper, Typography, CircularProgress, Alert, FormControl,
  InputLabel, Select, MenuItem, Grid, Chip, Button,
  LinearProgress, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow
} from '@mui/material';
import { Download as DownloadIcon } from '@mui/icons-material';
import api from '../../services/api';

const statusColors = { well_covered: 'success', in_progress: 'info', started: 'warning', not_started: 'default' };
const statusLabels = { well_covered: 'Well Covered', in_progress: 'In Progress', started: 'Started', not_started: 'Not Started' };

export default function CurriculumMapTab({ schoolId }) {
  const [grade, setGrade] = useState(10);
  const [classId, setClassId] = useState('');
  const [classes, setClasses] = useState([]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchClasses = async () => {
      try { const res = await api.get(`/api/schools/${schoolId}/classes`); setClasses(res.data.data || res.data.classes || []); } catch {}
    };
    fetchClasses();
  }, [schoolId]);

  useEffect(() => {
    const fetchMap = async () => {
      setLoading(true); setError(null);
      try {
        const params = { grade };
        if (classId) params.classId = classId;
        const res = await api.get(`/api/schools/${schoolId}/reports/curriculum-map`, { params });
        setData(res.data);
      } catch (err) {
        setError(err.response?.data?.error || 'Failed to load curriculum map');
      } finally { setLoading(false); }
    };
    fetchMap();
  }, [schoolId, grade, classId]);

  const handleDownloadCSV = () => {
    const params = new URLSearchParams({ grade });
    if (classId) params.set('classId', classId);
    window.open(`/api/schools/${schoolId}/reports/curriculum-map/csv?${params}`, '_blank');
  };

  return (
    <Box>
      {error && <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>{error}</Alert>}
      <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap', alignItems: 'center' }}>
        <FormControl size="small" sx={{ minWidth: 140 }}>
          <InputLabel>Grade</InputLabel>
          <Select value={grade} label="Grade" onChange={e => setGrade(e.target.value)}>
            {[8, 9, 10, 11, 12].map(g => <MenuItem key={g} value={g}>Grade {g}</MenuItem>)}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel>Filter by Class</InputLabel>
          <Select value={classId} label="Filter by Class" onChange={e => setClassId(e.target.value)}>
            <MenuItem value="">Whole School</MenuItem>
            {classes.filter(c => c.grade === grade).map(c => <MenuItem key={c._id} value={c._id}>{c.name}</MenuItem>)}
          </Select>
        </FormControl>
        <Button variant="outlined" size="small" startIcon={<DownloadIcon />} onClick={handleDownloadCSV}>Export CSV</Button>
      </Box>

      {loading && <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>}

      {!loading && data && (
        <>
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={6} sm={3}><Paper sx={{ p: 2, textAlign: 'center' }}><Typography variant="h4" fontWeight="bold">{data.learnerCount}</Typography><Typography variant="body2" color="text.secondary">Learners</Typography></Paper></Grid>
            <Grid item xs={6} sm={3}><Paper sx={{ p: 2, textAlign: 'center' }}><Typography variant="h4" fontWeight="bold">{data.topicsCovered}/{data.topicsTotal}</Typography><Typography variant="body2" color="text.secondary">Topics Covered</Typography></Paper></Grid>
            <Grid item xs={6} sm={3}><Paper sx={{ p: 2, textAlign: 'center' }}><Typography variant="h4" fontWeight="bold">{data.overallCoverage}%</Typography><Typography variant="body2" color="text.secondary">Overall Coverage</Typography></Paper></Grid>
            <Grid item xs={6} sm={3}><Paper sx={{ p: 2, textAlign: 'center' }}><Typography variant="h5" fontWeight="bold" color="primary">{data.className}</Typography><Typography variant="body2" color="text.secondary">Scope</Typography></Paper></Grid>
          </Grid>

          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>CAPS Topic</TableCell>
                  <TableCell align="center">Questions</TableCell>
                  <TableCell align="center">Learners</TableCell>
                  <TableCell align="center">Attempted</TableCell>
                  <TableCell align="center">Accuracy</TableCell>
                  <TableCell align="center">Coverage</TableCell>
                  <TableCell align="center">Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data.topics.map((t, i) => (
                  <TableRow key={i} hover>
                    <TableCell><Typography variant="body2" fontWeight="medium">{t.topic}</Typography></TableCell>
                    <TableCell align="center">{t.totalQuestions}</TableCell>
                    <TableCell align="center">{t.learnersAttempted}</TableCell>
                    <TableCell align="center">{t.questionsAttempted}</TableCell>
                    <TableCell align="center">
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                        <LinearProgress variant="determinate" value={t.averageAccuracy} sx={{ width: 60, height: 6, borderRadius: 3 }} color={t.averageAccuracy >= 70 ? 'success' : t.averageAccuracy >= 50 ? 'warning' : 'error'} />
                        <Typography variant="caption">{t.averageAccuracy}%</Typography>
                      </Box>
                    </TableCell>
                    <TableCell align="center">
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                        <LinearProgress variant="determinate" value={t.coveragePercent} sx={{ width: 60, height: 6, borderRadius: 3 }} color="primary" />
                        <Typography variant="caption">{t.coveragePercent}%</Typography>
                      </Box>
                    </TableCell>
                    <TableCell align="center"><Chip label={statusLabels[t.status]} size="small" color={statusColors[t.status]} variant="outlined" /></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}
    </Box>
  );
}
